import React, { useEffect, useState, useMemo, useCallback, memo, useRef, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Text,
    Spinner,
    Center,
    useToast,
    VStack,
    HStack,
    Heading,
    useColorModeValue,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Button,
    Badge,
    Card,
    CardBody,
    CardHeader,
    IconButton,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    SimpleGrid,
    Input,
    Textarea
} from '@chakra-ui/react';
import moment from 'moment';
import { newWorkoutService, WorkoutPlan } from 'services/newWorkoutService';
import { MdFitnessCenter, MdEventNote, MdInfo, MdChevronLeft, MdChevronRight, MdAdd, MdEdit, MdDelete } from 'react-icons/md';
import Icon from 'components/icons/Icon';
import DailyWorkoutEditor from 'components/workoutPlanCreator/DailyWorkoutEditor';
import ExerciseSelector from 'components/workoutPlanCreator/ExerciseSelector';

// FOCUS-PRESERVING INPUTS - Manage their own state to prevent focus loss
const FocusPreservingInput = memo(({
    initialValue,
    onValueChange,
    type = "text",
    ...props
}: {
    initialValue: string | number;
    onValueChange: (value: string) => void;
    type?: string;
    [key: string]: any;
}) => {
    const [value, setValue] = useState(initialValue.toString());

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setValue(newValue);
        onValueChange(newValue);
    };

    // Update internal state when initial value changes (from parent)
    useEffect(() => {
        setValue(initialValue.toString());
    }, [initialValue]);

    return (
        <Input
            type={type}
            value={value}
            onChange={handleChange}
            {...props}
        />
    );
});

const FocusPreservingTextarea = memo(({
    initialValue,
    onValueChange,
    ...props
}: {
    initialValue: string;
    onValueChange: (value: string) => void;
    [key: string]: any;
}) => {
    const [value, setValue] = useState(initialValue);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        setValue(newValue);
        onValueChange(newValue);
    };

    // Update internal state when initial value changes (from parent)
    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    return (
        <Textarea
            value={value}
            onChange={handleChange}
            {...props}
        />
    );
});

// SIMPLE EXERCISE CARD - No complex memoization, just works
const ExerciseCard = ({
    exercise,
    dayKey,
    exerciseIndex,
    onRemove,
    onUpdateField
}: {
    exercise: any;
    dayKey: string;
    exerciseIndex: number;
    onRemove: (dayKey: string, exerciseIndex: number) => void;
    onUpdateField: (dayKey: string, tempId: string, field: string, value: string | number) => void;
}) => {
    const cardBg = useColorModeValue('white', 'navy.700');
    const borderColor = useColorModeValue('blue.200', 'navy.600');

    return (
        <Card
            size="sm"
            variant="outline"
            bg={cardBg}
            borderColor={borderColor}
        >
            <CardBody p="3">
                <VStack align="stretch" spacing={2}>
                    {/* Exercise Header */}
                    <HStack justify="space-between" align="center">
                        <Text fontWeight="medium" fontSize="sm" noOfLines={1}>
                            {exercise.name}
                        </Text>
                        <IconButton
                            aria-label="Remove exercise"
                            icon={<Icon as={MdDelete} boxSize={3} />}
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => onRemove(dayKey, exerciseIndex)}
                        />
                    </HStack>

                    {/* Exercise Parameters */}
                    <SimpleGrid columns={2} spacing={2}>
                        <VStack align="start" spacing={1}>
                            <Text fontSize="xs" fontWeight="medium">Sets</Text>
                            <FocusPreservingInput
                                type="number"
                                initialValue={exercise.sets || 3}
                                onValueChange={(value) => onUpdateField(dayKey, exercise.tempId, 'sets', parseInt(value) || 3)}
                                placeholder="3"
                                size="xs"
                                min={1}
                                max={10}
                            />
                        </VStack>
                        <VStack align="start" spacing={1}>
                            <Text fontSize="xs" fontWeight="medium">Reps</Text>
                            <FocusPreservingInput
                                initialValue={exercise.reps || '10'}
                                onValueChange={(value) => onUpdateField(dayKey, exercise.tempId, 'reps', value)}
                                placeholder="10"
                                size="xs"
                            />
                        </VStack>
                        <VStack align="start" spacing={1}>
                            <Text fontSize="xs" fontWeight="medium">Weight</Text>
                            <FocusPreservingInput
                                initialValue={exercise.weight || ''}
                                onValueChange={(value) => onUpdateField(dayKey, exercise.tempId, 'weight', value)}
                                placeholder="kg"
                                size="xs"
                            />
                        </VStack>
                        <VStack align="start" spacing={1}>
                            <Text fontSize="xs" fontWeight="medium">Rest</Text>
                            <FocusPreservingInput
                                initialValue={exercise.rest_time || '60s'}
                                onValueChange={(value) => onUpdateField(dayKey, exercise.tempId, 'rest_time', value)}
                                placeholder="60s"
                                size="xs"
                            />
                        </VStack>
                    </SimpleGrid>

                    {/* Exercise Notes */}
                    <VStack align="start" spacing={1}>
                        <Text fontSize="xs" fontWeight="medium">Notes</Text>
                        <FocusPreservingTextarea
                            initialValue={exercise.notes || ''}
                            onValueChange={(value) => onUpdateField(dayKey, exercise.tempId, 'notes', value)}
                            placeholder="Add exercise notes..."
                            size="xs"
                            rows={2}
                            resize="vertical"
                        />
                    </VStack>
                </VStack>
            </CardBody>
        </Card>
    );
};

interface WorkoutEvent {
    workoutPlan: WorkoutPlan;
    notes?: string;
    title: string;
    start: Date;
    end: Date;
    allDay: boolean;
    dailyWorkout?: any;
    exercise?: any;
}

interface ClientWorkoutCalendarProps {
    clientId: string;
    clientName?: string;
    userRole?: string;
}

const ClientWorkoutCalendar: React.FC<ClientWorkoutCalendarProps> = ({ clientId, clientName, userRole }) => {
    const navigate = useNavigate();

    // Get the correct workout details URL based on user role
    const getWorkoutDetailsUrl = (workoutId: string) => {
        switch (userRole) {
            case 'admin':
                return `/admin/workouts/${workoutId}`;
            case 'agency':
                return `/agency/workouts/${workoutId}`;
            case 'trainer':
                return `/trainer/workouts/${workoutId}`;
            default:
                return `/`; // Default fallback
        }
    };

    const [workoutPlans, setWorkoutPlans] = useState<WorkoutPlan[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedEvent, setSelectedEvent] = useState<WorkoutEvent | null>(null);
    const [currentWeek, setCurrentWeek] = useState(moment()); // For custom week navigation
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isDailyWorkoutEditorOpen,
        onOpen: onDailyWorkoutEditorOpen,
        onClose: onDailyWorkoutEditorClose
    } = useDisclosure();
    const toast = useToast();

    // Quick Add Mode state
    const [isQuickAddMode, setIsQuickAddMode] = useState(false);
    const [weeklyExercises, setWeeklyExercises] = useState<{ [key: string]: any[] }>({});
    const [isExerciseSelectorOpen, setIsExerciseSelectorOpen] = useState(false);
    const [selectedDayForExercises, setSelectedDayForExercises] = useState<string | null>(null);
    const [isCreatingQuickPlan, setIsCreatingQuickPlan] = useState(false);

    // Navy theme color modes
    const bgColor = useColorModeValue('white', 'navy.800');
    const borderColor = useColorModeValue('gray.200', 'navy.600');
    const cardBg = useColorModeValue('white', 'navy.700');
    const activeEventColor = useColorModeValue('#3182CE', '#63B3ED');
    const inactiveEventColor = useColorModeValue('#A0AEC0', '#4A5568');
    const textColor = useColorModeValue('gray.800', 'white');
    const mutedTextColor = useColorModeValue('gray.600', 'gray.400');

    useEffect(() => {
        if (!clientId) {
            setError('No client selected');
            setLoading(false);
            return;
        }

        const fetchPlans = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await newWorkoutService.getWorkoutPlans({
                    client_id: clientId,
                    page: 1,
                    limit: 100,
                    active_only: true
                });

                if (!response) {
                    throw new Error('No response received from workout service');
                }

                const plans = response.workout_plans || [];
                setWorkoutPlans(plans);
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Failed to load workout plans';
                setError(errorMessage);
                toast({
                    title: 'Error Loading Workout Plans',
                    description: errorMessage,
                    status: 'error',
                    duration: 5000,
                    isClosable: true
                });
            } finally {
                setLoading(false);
            }
        };

        fetchPlans();
    }, [clientId, toast]);

    // Function to refresh workout plans after creating a new one
    const refreshWorkoutPlans = useCallback(async () => {
        if (!clientId) return;

        setLoading(true);
        try {
            const response = await newWorkoutService.getWorkoutPlans({
                client_id: clientId,
                page: 1,
                limit: 100,
                active_only: true
            });
            setWorkoutPlans(response.workout_plans || []);
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to refresh workout plans',
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        } finally {
            setLoading(false);
        }
    }, [clientId, toast]);

    // Cleanup timeouts on unmount to prevent memory leaks
    useEffect(() => {
        return () => {
            Object.values(updateTimeoutRef.current).forEach(timeout => {
                clearTimeout(timeout);
            });
        };
    }, []);

    // Quick Add Mode Functions
    const handleQuickAddToggle = () => {
        if (isQuickAddMode) {
            // Reset quick add mode
            setIsQuickAddMode(false);
            setWeeklyExercises({});
        } else {
            // Enter quick add mode
            setIsQuickAddMode(true);
        }
    };

    const handleAddExercisesToDay = useCallback((date: moment.Moment) => {
        const dayKey = date.format('YYYY-MM-DD');
        setSelectedDayForExercises(dayKey);
        setIsExerciseSelectorOpen(true);
    }, []);

    // DEBOUNCED UPDATE SYSTEM - Prevents focus loss by delaying state updates
    const updateTimeoutRef = useRef<{ [key: string]: NodeJS.Timeout }>({});
    const pendingUpdatesRef = useRef<{ [key: string]: any }>({});

    const updateExerciseField = useCallback((dayKey: string, tempId: string, field: string, value: string | number) => {
        const updateKey = `${dayKey}-${tempId}-${field}`;

        // Store the pending update
        pendingUpdatesRef.current[updateKey] = { dayKey, tempId, field, value };

        // Clear existing timeout for this field
        if (updateTimeoutRef.current[updateKey]) {
            clearTimeout(updateTimeoutRef.current[updateKey]);
        }

        // Set a new timeout to batch the update
        updateTimeoutRef.current[updateKey] = setTimeout(() => {
            const updates = { ...pendingUpdatesRef.current };
            pendingUpdatesRef.current = {};

            setWeeklyExercises(prev => {
                const newExercises = { ...prev };

                // Apply all pending updates in one batch
                Object.values(updates).forEach(({ dayKey, tempId, field, value }) => {
                    if (newExercises[dayKey]) {
                        const exerciseIndex = newExercises[dayKey].findIndex(ex => ex.tempId === tempId);
                        if (exerciseIndex !== -1) {
                            newExercises[dayKey][exerciseIndex] = {
                                ...newExercises[dayKey][exerciseIndex],
                                [field]: value
                            };
                        }
                    }
                });

                return newExercises;
            });

            // Clean up timeout refs
            Object.keys(updates).forEach(key => {
                delete updateTimeoutRef.current[key];
            });
        }, 1000); // 1000ms (1 second) debounce delay
    }, []);

    const removeExercise = useCallback((dayKey: string, exerciseIndex: number) => {
        setWeeklyExercises(prev => ({
            ...prev,
            [dayKey]: prev[dayKey].filter((_, index) => index !== exerciseIndex)
        }));
    }, []);

    const handleExercisesSelected = (exercises: any[]) => {
        if (selectedDayForExercises) {
            // Create exercises with default parameters and unique IDs
            const exercisesWithParams = exercises.map(exercise => ({
                ...exercise,
                tempId: `${exercise.id || exercise.name}-${Date.now()}-${Math.random()}`, // Unique temporary ID
                sets: 3,
                reps: '10',
                weight: '',
                rest_time: '60s',
                notes: '',
                order_index: 0 // Will be set when creating the plan
            }));

            setWeeklyExercises(prev => ({
                ...prev,
                [selectedDayForExercises]: [...(prev[selectedDayForExercises] || []), ...exercisesWithParams]
            }));
        }
        setIsExerciseSelectorOpen(false);
        setSelectedDayForExercises(null);
    };

    const handleSubmitQuickPlan = async () => {
        const hasExercises = Object.values(weeklyExercises).some(exercises => exercises.length > 0);

        if (!hasExercises) {
            toast({
                title: 'No Exercises Added',
                description: 'Please add at least one exercise to create a plan',
                status: 'warning',
                duration: 3000,
                isClosable: true
            });
            return;
        }

        setIsCreatingQuickPlan(true);
        try {
            const weekStart = currentWeek.clone().startOf('isoWeek');
            const planTitle = `${clientName || 'Client'} - Week ${weekStart.format('MMM D')}`;

            // Build daily workouts for the week
            const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
            const dailyWorkouts = days.map((dayName, index) => {
                const dayDate = weekStart.clone().add(index, 'days');
                const dayKey = dayDate.format('YYYY-MM-DD');
                const dayExercises = weeklyExercises[dayKey] || [];

                return {
                    day_number: index + 1,
                    day_of_week: dayName,
                    workout_date: dayDate.toDate(),
                    title: dayExercises.length > 0 ? `${dayName} Workout` : `${dayName} Rest`,
                    description: dayExercises.length > 0 ? `${dayExercises.length} exercises planned` : 'Rest day',
                    estimated_duration: dayExercises.length * 15, // 15 min per exercise
                    is_rest_day: dayExercises.length === 0,
                    notes: dayExercises.length > 0 ? 'Added via quick calendar planner' : 'Rest and recovery',
                    exercises: dayExercises.map((exercise: any, exerciseIndex: number) => ({
                        exercise_id: exercise.id,
                        order_index: exerciseIndex,
                        sets: exercise.sets || 3,
                        reps: exercise.reps || '10',
                        weight: exercise.weight || '',
                        rest_time: exercise.rest_time || '60s',
                        notes: exercise.notes || ''
                    }))
                };
            });

            const params = {
                title: planTitle,
                client_id: clientId,
                start_date: weekStart.toDate(),
                description: `Quick workout plan for the week of ${weekStart.format('MMMM Do, YYYY')}`,
                goals: 'Weekly fitness routine',
                notes: 'Created using quick calendar planner',
                difficulty: 'intermediate',
                plan_type: 'mixed',
                daily_workouts: dailyWorkouts
            };

            await newWorkoutService.createWorkoutPlan(params);

            toast({
                title: 'Quick Plan Created!',
                description: `Week plan "${planTitle}" created successfully`,
                status: 'success',
                duration: 3000,
                isClosable: true
            });

            // Reset state and refresh
            setIsQuickAddMode(false);
            setWeeklyExercises({});
            refreshWorkoutPlans();

        } catch (error) {
            toast({
                title: 'Error',
                description: error instanceof Error ? error.message : 'Failed to create quick plan',
                status: 'error',
                duration: 5000,
                isClosable: true
            });
        } finally {
            setIsCreatingQuickPlan(false);
        }
    };

    // Map daily workouts to calendar events - only daily exercises
    const events: WorkoutEvent[] = useMemo(() => {
        // Show individual exercises from daily workouts
        return workoutPlans.flatMap(plan => {
            if (!plan.daily_workouts?.length) {
                return [];
            }

            // Map daily workouts to individual exercise events
            return plan.daily_workouts
                .filter(dw => dw && dw.workout_date) // Filter out invalid workouts
                .flatMap(dw => {
                    try {
                        // Try parsing the date in multiple formats
                        let date = moment(dw.workout_date);
                        if (!date.isValid()) {
                            date = moment(dw.workout_date, 'YYYY-MM-DD');
                        }
                        if (!date.isValid()) {
                            return [];
                        }

                        const formattedDate = date.format('YYYY-MM-DD');

                        // If it's a rest day, show one event
                        if (dw.is_rest_day) {
                            return [{
                                title: `🛌 Rest Day`,
                                start: date.startOf('day').toDate(),
                                end: date.endOf('day').toDate(),
                                allDay: true,
                                workoutPlan: plan,
                                dailyWorkout: dw,
                                notes: dw.notes || 'Rest and recovery day',
                            } as WorkoutEvent];
                        }

                        // Check exercises field
                        const exercises = dw.exercises || [];

                        // If there are exercises, show them as all-day events to ensure visibility in month view
                        if (exercises && exercises.length > 0) {
                            return exercises.map((exercise, index) => {
                                // Try to get exercise name from different possible fields
                                const exerciseName = exercise.exercise?.name ||
                                    `Exercise ${index + 1}`;

                                return {
                                    title: `💪 ${exerciseName}`,
                                    start: date.startOf('day').toDate(),
                                    end: date.endOf('day').toDate(),
                                    allDay: true, // Changed to allDay to ensure visibility in month view
                                    workoutPlan: plan,
                                    dailyWorkout: dw,
                                    exercise: exercise,
                                    notes: `Sets: ${exercise.sets || 'N/A'} | Reps: ${exercise.reps || 'N/A'} | Weight: ${exercise.weight || 'N/A'}${exercise.notes ? ` | Notes: ${exercise.notes}` : ''}`,
                                } as WorkoutEvent;
                            });
                        } else {
                            // If no exercises found, show the workout itself with a note
                            return [{
                                title: `🏋️ ${dw.title || 'Workout Session'} (No exercises found)`,
                                start: date.startOf('day').toDate(),
                                end: date.endOf('day').toDate(),
                                allDay: true,
                                workoutPlan: plan,
                                dailyWorkout: dw,
                                notes: dw.notes || 'Workout session - exercises may need to be added',
                            } as WorkoutEvent];
                        }
                    } catch (error) {
                        return [];
                    }
                })
                .flat() // Flatten the array since we're returning arrays of events
                .filter((event): event is WorkoutEvent => !!event); // Remove undefined events and ensure correct type
        });
    }, [workoutPlans]);

    // Calculate statistics
    const stats = useMemo(() => {
        const totalPlans = workoutPlans.length;
        const activePlans = workoutPlans.filter(plan => plan.is_active).length;
        const totalWorkouts = workoutPlans.reduce((acc, plan) => acc + (plan.daily_workouts?.length || 0), 0);
        const restDays = workoutPlans.reduce((acc, plan) =>
            acc + (plan.daily_workouts?.filter(dw => dw.is_rest_day).length || 0), 0);
        const workoutDays = totalWorkouts - restDays;

        return {
            totalPlans,
            activePlans,
            totalWorkouts,
            workoutDays,
            restDays,
            completionRate: totalWorkouts > 0 ? Math.round((workoutDays / totalWorkouts) * 100) : 0
        };
    }, [workoutPlans]);

    const handleEventClick = useCallback((event: WorkoutEvent) => {
        setSelectedEvent(event);
        onOpen();
    }, [onOpen]);

    // Helper function to get week days
    const getWeekDays = useCallback(() => {
        const weekStart = currentWeek.clone().startOf('isoWeek');
        const days = [];
        for (let i = 0; i < 7; i++) {
            days.push(weekStart.clone().add(i, 'days'));
        }
        return days;
    }, [currentWeek]);

    // Get events for a specific day
    const getEventsForDay = useCallback((day: moment.Moment) => {
        return events.filter(event =>
            moment(event.start).format('YYYY-MM-DD') === day.format('YYYY-MM-DD')
        );
    }, [events]);

    // Vertical Week View Component
    const VerticalWeekView = () => {
        const weekDays = getWeekDays();

        return (
            <Card
                bg={cardBg}
                border="none"
                borderRadius="20px"
                overflow="hidden"
                boxShadow={useColorModeValue('0 4px 12px rgba(0,0,0,0.05)', '0 4px 12px rgba(0,0,0,0.2)')}
            >
                <CardHeader
                    bg={useColorModeValue('gray.50', 'navy.800')}
                    borderBottom="none"
                    py={{ base: 4, md: 6 }}
                    px={{ base: 4, md: 6 }}
                >
                    <HStack justify="space-between" align="center">
                        <VStack align="start" spacing={1}>
                            <Text fontWeight="700" color={textColor} fontSize={{ base: "lg", md: "xl" }}>
                                Week View
                            </Text>
                            <Text color={mutedTextColor} fontSize={{ base: "sm", md: "md" }} fontWeight="500">
                                {currentWeek.clone().startOf('isoWeek').format('MMM D')} - {currentWeek.clone().endOf('isoWeek').format('MMM D, YYYY')}
                            </Text>
                        </VStack>
                        <HStack spacing={2}>
                            <Button
                                size="sm"
                                colorScheme={isQuickAddMode ? "blue" : "green"}
                                leftIcon={<Icon as={isQuickAddMode ? MdFitnessCenter : MdAdd} />}
                                onClick={isQuickAddMode ? handleSubmitQuickPlan : handleQuickAddToggle}
                                variant="solid"
                                boxShadow={isQuickAddMode ? "0 2px 8px rgba(59, 130, 246, 0.2)" : "0 2px 8px rgba(34, 197, 94, 0.2)"}
                                _hover={{
                                    transform: 'translateY(-1px)',
                                    boxShadow: isQuickAddMode ? '0 4px 12px rgba(59, 130, 246, 0.3)' : '0 4px 12px rgba(34, 197, 94, 0.3)'
                                }}
                                transition="all 0.2s ease"
                                title={isQuickAddMode ? "Submit quick plan" : "Quick Add feature temporarily disabled"}
                                isLoading={isCreatingQuickPlan}
                                loadingText="Creating..."
                                isDisabled={true}
                                opacity={0.5}
                                cursor="not-allowed"
                            >
                                {isQuickAddMode ? "Submit Plan" : "Quick Add"}
                            </Button>
                            {isQuickAddMode && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleQuickAddToggle}
                                    title="Cancel quick add mode"
                                >
                                    Cancel
                                </Button>
                            )}
                            <IconButton
                                aria-label="Previous week"
                                icon={<Icon as={MdChevronLeft} />}
                                onClick={() => setCurrentWeek(prev => prev.clone().subtract(1, 'week'))}
                                variant="outline"
                                size="sm"
                            />
                            <Button
                                onClick={() => setCurrentWeek(moment())}
                                variant="outline"
                                size="sm"
                                fontSize="xs"
                            >
                                Today
                            </Button>
                            <IconButton
                                aria-label="Next week"
                                icon={<Icon as={MdChevronRight} />}
                                onClick={() => setCurrentWeek(prev => prev.clone().add(1, 'week'))}
                                variant="outline"
                                size="sm"
                            />
                        </HStack>
                    </HStack>
                </CardHeader>

                {/* Quick Add Mode Indicator */}
                {isQuickAddMode && (
                    <Alert
                        status="info"
                        variant="subtle"
                        bg={useColorModeValue('blue.50', 'navy.900')}
                        borderColor={useColorModeValue('blue.200', 'navy.600')}
                        borderWidth="1px"
                        borderRadius="none"
                        borderLeft="none"
                        borderRight="none"
                    >
                        <AlertIcon />
                        <VStack align="start" spacing={1} flex={1}>
                            <Text fontSize="sm" fontWeight="semibold">
                                Quick Add Mode Active
                            </Text>
                            <Text fontSize="xs" color={useColorModeValue('blue.600', 'blue.200')}>
                                Click "Add Exercises" on any day to build your weekly plan. Click "Submit Plan" when ready.
                            </Text>
                        </VStack>
                        {Object.keys(weeklyExercises).length > 0 && (
                            <Badge colorScheme="blue" ml={2}>
                                {Object.values(weeklyExercises).reduce((total, exercises) => total + exercises.length, 0)} exercises added
                            </Badge>
                        )}
                    </Alert>
                )}

                <CardBody p="0">
                    <VStack spacing={0} align="stretch">
                        {weekDays.map((day, index) => {
                            const dayEvents = getEventsForDay(day);
                            const isToday = day.isSame(moment(), 'day');
                            const dayKey = day.format('YYYY-MM-DD'); // Cache the day key

                            return (
                                <Box
                                    key={index}
                                    borderBottom={index < 6 ? "1px" : "none"}
                                    borderColor={useColorModeValue('gray.300', 'navy.600')}
                                    bg={isToday ? useColorModeValue('blue.25', 'navy.900') : 'transparent'}
                                    _hover={{
                                        bg: useColorModeValue('gray.25', 'navy.800')
                                    }}
                                    transition="all 0.2s ease"
                                >
                                    <HStack align="start" spacing={0} minH="80px">
                                        {/* Day Header */}
                                        <Box
                                            w="120px"
                                            p={4}
                                            borderRight="1px"
                                            borderColor={useColorModeValue('gray.100', 'navy.600')}
                                            bg={isToday ? useColorModeValue('blue.50', 'navy.900') : useColorModeValue('gray.50', 'navy.800')}
                                        >
                                            <VStack align="start" spacing={1}>
                                                <Text
                                                    fontSize="xs"
                                                    fontWeight="600"
                                                    color={isToday ? useColorModeValue('blue.600', 'blue.200') : mutedTextColor}
                                                    textTransform="uppercase"
                                                    letterSpacing="wider"
                                                >
                                                    {day.format('dddd')}
                                                </Text>
                                                <Text
                                                    fontSize="2xl"
                                                    fontWeight="700"
                                                    color={isToday ? useColorModeValue('blue.700', 'blue.100') : textColor}
                                                >
                                                    {day.format('DD')}
                                                </Text>
                                                <Text
                                                    fontSize="xs"
                                                    color={isToday ? useColorModeValue('blue.600', 'blue.200') : mutedTextColor}
                                                    fontWeight="500"
                                                >
                                                    {day.format('MMM')}
                                                </Text>
                                            </VStack>
                                        </Box>

                                        {/* Exercises for the day */}
                                        <Box flex={1} p={4}>
                                            {dayEvents.length > 0 ? (
                                                <VStack align="stretch" spacing={3}>
                                                    {dayEvents.map((event, eventIndex) => {
                                                        // Simple exercise display
                                                        if (event.dailyWorkout?.is_rest_day) {
                                                            return (
                                                                <Box
                                                                    key={eventIndex}
                                                                    p={3}
                                                                    borderRadius="lg"
                                                                    bg={useColorModeValue('orange.50', 'orange.900')}
                                                                    border="1px"
                                                                    borderColor={useColorModeValue('orange.200', 'orange.700')}
                                                                    cursor="pointer"
                                                                    onClick={() => handleEventClick(event)}
                                                                    _hover={{
                                                                        transform: 'translateY(-1px)',
                                                                        boxShadow: 'md'
                                                                    }}
                                                                    transition="all 0.2s ease"
                                                                >
                                                                    <HStack>
                                                                        <Text fontSize="lg">😴</Text>
                                                                        <Text fontWeight="600" color={useColorModeValue('orange.700', 'orange.100')}>
                                                                            Rest Day
                                                                        </Text>
                                                                    </HStack>
                                                                </Box>
                                                            );
                                                        }

                                                        // Regular workout display - simplified
                                                        if (event.exercise) {
                                                            return (
                                                                <Box
                                                                    key={eventIndex}
                                                                    p={3}
                                                                    borderRadius="lg"
                                                                    bg={useColorModeValue('blue.50', 'blue.900')}
                                                                    border="1px"
                                                                    borderColor={useColorModeValue('blue.200', 'blue.700')}
                                                                    cursor="pointer"
                                                                    onClick={() => handleEventClick(event)}
                                                                    _hover={{
                                                                        transform: 'translateY(-1px)',
                                                                        boxShadow: 'md'
                                                                    }}
                                                                    transition="all 0.2s ease"
                                                                >
                                                                    <VStack align="start" spacing={1}>
                                                                        <HStack justify="space-between" width="100%">
                                                                            <Text fontWeight="600" fontSize="sm" color={useColorModeValue('blue.700', 'blue.100')}>
                                                                                {event.exercise.exercise?.name || 'Exercise'}
                                                                            </Text>
                                                                            {event.exercise.notes && (
                                                                                <Text fontSize="xs" color={useColorModeValue('blue.500', 'blue.300')}>
                                                                                    💭
                                                                                </Text>
                                                                            )}
                                                                        </HStack>
                                                                        <HStack spacing={3} fontSize="xs" color={useColorModeValue('blue.600', 'blue.200')}>
                                                                            <Text fontWeight="500">
                                                                                {event.exercise.sets || 0}x{event.exercise.reps || 0}
                                                                            </Text>
                                                                            {event.exercise.weight && (
                                                                                <Text fontWeight="500">
                                                                                    {event.exercise.weight}kg
                                                                                </Text>
                                                                            )}
                                                                        </HStack>
                                                                    </VStack>
                                                                </Box>
                                                            );
                                                        }

                                                        // Workout summary when multiple exercises
                                                        if (event.dailyWorkout && event.dailyWorkout.exercises?.length > 0) {
                                                            return (
                                                                <Box
                                                                    key={eventIndex}
                                                                    p={3}
                                                                    borderRadius="lg"
                                                                    bg={useColorModeValue('green.50', 'green.900')}
                                                                    border="1px"
                                                                    borderColor={useColorModeValue('green.200', 'green.700')}
                                                                    cursor="pointer"
                                                                    onClick={() => handleEventClick(event)}
                                                                    _hover={{
                                                                        transform: 'translateY(-1px)',
                                                                        boxShadow: 'md'
                                                                    }}
                                                                    transition="all 0.2s ease"
                                                                >
                                                                    <VStack align="start" spacing={2}>
                                                                        <HStack>
                                                                            <Text fontSize="sm">🏋️</Text>
                                                                            <Text fontWeight="600" fontSize="sm" color={useColorModeValue('green.700', 'green.100')}>
                                                                                {event.dailyWorkout.title || 'Workout'}
                                                                            </Text>
                                                                        </HStack>
                                                                        <Text fontSize="xs" color={useColorModeValue('green.600', 'green.200')}>
                                                                            {event.dailyWorkout.exercises.length} exercises
                                                                        </Text>
                                                                        {/* Show first 2 exercises as preview */}
                                                                        <VStack align="start" spacing={0.5}>
                                                                            {event.dailyWorkout.exercises.slice(0, 2).map((exercise: any, idx: number) => (
                                                                                <Text key={idx} fontSize="xs" color={useColorModeValue('green.600', 'green.300')}>
                                                                                    • {exercise.exercise?.name || `Exercise ${idx + 1}`}
                                                                                </Text>
                                                                            ))}
                                                                            {event.dailyWorkout.exercises.length > 2 && (
                                                                                <Text fontSize="xs" color={useColorModeValue('green.500', 'green.400')} fontStyle="italic">
                                                                                    +{event.dailyWorkout.exercises.length - 2} more...
                                                                                </Text>
                                                                            )}
                                                                        </VStack>
                                                                    </VStack>
                                                                </Box>
                                                            );
                                                        }

                                                        return null;
                                                    })}
                                                </VStack>
                                            ) : (
                                                <Box width="100%">
                                                    {isQuickAddMode ? (
                                                        <VStack align="stretch" spacing={2}>
                                                            <HStack justify="flex-end" align="center" width="100%">
                                                                <Button
                                                                    size="xs"
                                                                    colorScheme="blue"
                                                                    variant="outline"
                                                                    leftIcon={<Icon as={MdAdd} />}
                                                                    onClick={() => handleAddExercisesToDay(day)}
                                                                    _hover={{
                                                                        transform: 'scale(1.05)',
                                                                        borderColor: 'blue.400'
                                                                    }}
                                                                    transition="all 0.2s ease"
                                                                >
                                                                    Add Exercises
                                                                </Button>
                                                            </HStack>
                                                            {weeklyExercises[dayKey] && (
                                                                <VStack align="stretch" spacing={2}>
                                                                    {weeklyExercises[dayKey].map((exercise: any, exerciseIndex: number) => (
                                                                        <ExerciseCard
                                                                            key={exercise.tempId}
                                                                            exercise={exercise}
                                                                            dayKey={dayKey}
                                                                            exerciseIndex={exerciseIndex}
                                                                            onRemove={removeExercise}
                                                                            onUpdateField={updateExerciseField}
                                                                        />
                                                                    ))}
                                                                </VStack>
                                                            )}
                                                        </VStack>
                                                    ) : (
                                                        <HStack justify="center" align="center" width="100%">
                                                            <Text fontSize="sm" color={mutedTextColor} fontStyle="italic">
                                                                No workouts scheduled
                                                            </Text>
                                                        </HStack>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    </HStack>
                                </Box>
                            );
                        })}
                    </VStack>
                </CardBody>
            </Card>
        );
    };

    return (
        <SimpleGrid columns={1} spacing={0} w="full">
            <Box px={0} py={0} position="relative" mt={6}>
                <VStack align="stretch" spacing={{ base: 4, md: 6, lg: 8 }}>

                    {/* Premium Content Section */}
                    {loading ? (
                        <Card
                            bg={cardBg}
                            backdropFilter="blur(20px)"
                            borderColor={borderColor}
                            border="1px"
                            borderRadius="20px"
                        >
                            <CardBody>
                                <Center h="500px">
                                    <VStack spacing={6}>
                                        <Box position="relative">
                                            <Spinner size="xl" color="blue.500" thickness="4px" />
                                            <Box
                                                position="absolute"
                                                top="50%"
                                                left="50%"
                                                transform="translate(-50%, -50%)"
                                                w="80px"
                                                h="80px"
                                                border="2px solid"
                                                borderColor="blue.200"
                                                borderRadius="full"
                                                animation="ping 2s cubic-bezier(0, 0, 0.2, 1) infinite"
                                            />
                                        </Box>
                                        <VStack spacing={2}>
                                            <Text color={textColor} fontSize="xl" fontWeight="600">Loading workout plans...</Text>
                                            <Text color={mutedTextColor} fontSize="md">
                                                Fetching premium training schedule for {clientName || 'client'}
                                            </Text>
                                        </VStack>
                                    </VStack>
                                </Center>
                            </CardBody>
                        </Card>
                    ) : error ? (
                        <Alert
                            status="error"
                            borderRadius="20px"
                            bg={useColorModeValue('red.50', 'red.900')}
                            border="1px"
                            borderColor={useColorModeValue('red.200', 'red.600')}
                            backdropFilter="blur(10px)"
                        >
                            <AlertIcon />
                            <Box flex="1">
                                <AlertTitle fontWeight="700">Failed to load workout plans!</AlertTitle>
                                <AlertDescription fontWeight="500">{error}</AlertDescription>
                            </Box>
                            <Button
                                size="md"
                                colorScheme="red"
                                variant="outline"
                                onClick={() => window.location.reload()}
                                borderRadius="12px"
                                fontWeight="600"
                            >
                                Retry
                            </Button>
                        </Alert>
                    ) : workoutPlans.length === 0 ? (
                        <Card
                            bg={cardBg}
                            border="none"
                            borderRadius="20px"
                            overflow="hidden"
                            boxShadow={useColorModeValue('0 4px 12px rgba(0,0,0,0.05)', '0 4px 12px rgba(0,0,0,0.2)')}
                        >
                            <CardHeader
                                bg={useColorModeValue('gray.50', 'navy.800')}
                                borderBottom="none"
                                py={{ base: 4, md: 6 }}
                                px={{ base: 4, md: 6 }}
                            >
                                <VStack spacing={{ base: 3, md: 4 }} align="stretch">
                                    <HStack justify="space-between" align="center" wrap="wrap" spacing={{ base: 2, md: 4 }}>
                                        <VStack align="start" spacing={1}>
                                            <Text fontWeight="700" color={textColor} fontSize={{ base: "lg", md: "xl" }}>Weekly Training Schedule</Text>
                                        </VStack>

                                    </HStack>
                                </VStack>
                            </CardHeader>
                            <CardBody p="0">
                                <VerticalWeekView />
                            </CardBody>
                        </Card>
                    ) : (
                        <Card
                            bg={cardBg}
                            border="none"
                            borderRadius="20px"
                            overflow="hidden"
                            boxShadow={useColorModeValue('0 4px 12px rgba(0,0,0,0.05)', '0 4px 12px rgba(0,0,0,0.2)')}
                        >
                            <CardHeader
                                bg={useColorModeValue('gray.50', 'navy.800')}
                                borderBottom="none"
                                py={{ base: 4, md: 6 }}
                                px={{ base: 4, md: 6 }}
                            >
                                <VStack spacing={{ base: 3, md: 4 }} align="stretch">
                                    <HStack justify="space-between" align="center" wrap="wrap" spacing={{ base: 2, md: 4 }}>
                                        <VStack align="start" spacing={1}>
                                            <Text fontWeight="700" color={textColor} fontSize={{ base: "lg", md: "xl" }}>Weekly Training Schedule</Text>
                                            <Text color={mutedTextColor} fontSize={{ base: "sm", md: "md" }} fontWeight="500">
                                                {workoutPlans.length} plan(s) • {events.length} scheduled workout(s)
                                            </Text>
                                        </VStack>
                                        <HStack spacing={{ base: 3, md: 6 }}>
                                            <HStack spacing={3}>
                                                <Box w={4} h={4} bg={activeEventColor} borderRadius="full" boxShadow="0 0 10px rgba(59, 130, 246, 0.5)" />
                                                <Text fontSize="sm" color={textColor} fontWeight="600">Active</Text>
                                            </HStack>
                                            <HStack spacing={3}>
                                                <Box w={4} h={4} bg={inactiveEventColor} borderRadius="full" />
                                                <Text fontSize="sm" color={textColor} fontWeight="600">Inactive</Text>
                                            </HStack>
                                        </HStack>
                                    </HStack>
                                </VStack>
                            </CardHeader>
                            <CardBody p="0">
                                <VerticalWeekView />
                            </CardBody>
                        </Card>
                    )}
                </VStack>
            </Box>

            {/* Enhanced Workout Detail Modal */}
            <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
                <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
                <ModalContent borderRadius="xl" border="1px" borderColor={useColorModeValue('gray.200', 'navy.600')} bg={useColorModeValue('white', 'navy.800')} maxH="90vh" overflow="auto">
                    <ModalHeader>
                        <HStack spacing={3}>
                            <Icon as={MdInfo} color="blue.500" boxSize={6} />
                            <VStack align="start" spacing={0}>
                                <Text fontWeight="700" fontSize="lg">Workout Details</Text>
                                <Text fontSize="sm" color={useColorModeValue('gray.600', 'gray.400')}>
                                    Daily Exercise Information
                                </Text>
                            </VStack>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton />

                    <ModalBody pb={6}>
                        {selectedEvent && (
                            <VStack align="stretch" spacing={6}>
                                {/* Title and Status */}
                                <Card borderRadius="lg" bg={useColorModeValue('gray.50', 'navy.700')}>
                                    <CardBody>
                                        <VStack align="stretch" spacing={3}>

                                            <HStack spacing={2} flexWrap="wrap">
                                                <Badge colorScheme={selectedEvent.workoutPlan.is_active ? "green" : "gray"} fontSize="sm" px={3} py={1}>
                                                    {selectedEvent.workoutPlan.is_active ? "Active" : "Inactive"}
                                                </Badge>
                                                <Badge colorScheme="blue" fontSize="sm" px={3} py={1}>
                                                    Daily Exercise
                                                </Badge>
                                                {selectedEvent.dailyWorkout?.is_rest_day && (
                                                    <Badge colorScheme="orange" fontSize="sm" px={3} py={1}>
                                                        Rest Day
                                                    </Badge>
                                                )}
                                            </HStack>
                                        </VStack>
                                    </CardBody>
                                </Card>

                                {/* Date Information */}
                                <Box>
                                    <Text fontSize="sm" color={useColorModeValue('gray.600', 'gray.400')} mb={2} fontWeight="600">
                                        📅 Date Information
                                    </Text>
                                    <Card borderRadius="lg" bg={useColorModeValue('white', 'navy.700')}>
                                        <CardBody>
                                            <Text fontWeight="500" fontSize="lg">
                                                {moment(selectedEvent.start).format('dddd, MMMM Do, YYYY')}
                                            </Text>
                                        </CardBody>
                                    </Card>
                                </Box>

                                {/* Daily Workout Summary - Show all exercises for this day */}
                                {selectedEvent.dailyWorkout && !selectedEvent.dailyWorkout.is_rest_day && (
                                    <Box>
                                        <Text fontSize="sm" color={useColorModeValue('gray.600', 'gray.400')} mb={2} fontWeight="600">
                                            📋 Complete Workout Summary
                                        </Text>
                                        <Card borderRadius="lg" bg={useColorModeValue('white', 'navy.700')}>
                                            <CardBody>
                                                <VStack align="stretch" spacing={3}>
                                                    <Text fontWeight="600" fontSize="md" mb={2}>
                                                        {selectedEvent.dailyWorkout.title || 'Daily Workout'}
                                                    </Text>

                                                    {selectedEvent.dailyWorkout.exercises && selectedEvent.dailyWorkout.exercises.length > 0 ? (
                                                        <VStack align="stretch" spacing={2}>
                                                            {selectedEvent.dailyWorkout.exercises.map((exercise: any, index: number) => (
                                                                <Box
                                                                    key={index}
                                                                    p={3}
                                                                    borderRadius="md"
                                                                    bg={useColorModeValue('gray.50', 'navy.700')}
                                                                    border="1px"
                                                                    borderColor={useColorModeValue('gray.200', 'navy.600')}
                                                                >
                                                                    <HStack justify="space-between" align="center">
                                                                        <VStack align="start" spacing={0}>
                                                                            <Text fontWeight="600" fontSize="sm">
                                                                                {String.fromCharCode(65 + index)}. {exercise.exercise?.name || `Exercise ${index + 1}`}
                                                                            </Text>
                                                                            <Text fontSize="xs" color={useColorModeValue('gray.600', 'gray.400')}>
                                                                                {exercise.exercise?.category || 'Exercise'}
                                                                            </Text>
                                                                        </VStack>
                                                                        <HStack spacing={4} fontSize="sm" fontWeight="500">
                                                                            <Text color={useColorModeValue('blue.600', 'blue.300')}>
                                                                                {exercise.sets || '0'}x{exercise.reps || '0'}
                                                                            </Text>
                                                                            {exercise.weight && (
                                                                                <Text color={useColorModeValue('green.600', 'green.300')}>
                                                                                    {exercise.weight}kg
                                                                                </Text>
                                                                            )}
                                                                        </HStack>
                                                                    </HStack>
                                                                    {exercise.notes && (
                                                                        <Text fontSize="xs" color={useColorModeValue('gray.500', 'gray.400')} mt={1}>
                                                                            💭 {exercise.notes}
                                                                        </Text>
                                                                    )}
                                                                </Box>
                                                            ))}
                                                        </VStack>
                                                    ) : (
                                                        <Text fontSize="sm" color={useColorModeValue('gray.500', 'gray.400')} fontStyle="italic">
                                                            No exercises found for this workout session.
                                                        </Text>
                                                    )}

                                                    {selectedEvent.dailyWorkout.notes && (
                                                        <Box pt={3} borderTop="1px" borderColor={useColorModeValue('gray.200', 'navy.600')}>
                                                            <Text fontSize="sm" color={useColorModeValue('gray.600', 'gray.400')} fontWeight="500">
                                                                📝 Workout Notes:
                                                            </Text>
                                                            <Text fontSize="sm" mt={1}>{selectedEvent.dailyWorkout.notes}</Text>
                                                        </Box>
                                                    )}
                                                </VStack>
                                            </CardBody>
                                        </Card>
                                    </Box>
                                )}

                                {/* Rest Day Information */}
                                {selectedEvent.dailyWorkout?.is_rest_day && (
                                    <Box>
                                        <Text fontSize="sm" color={useColorModeValue('gray.600', 'gray.400')} mb={2} fontWeight="600">
                                            😴 Rest Day Information
                                        </Text>
                                        <Card borderRadius="lg" bg={useColorModeValue('orange.50', 'orange.900')}>
                                            <CardBody>
                                                <VStack align="center" spacing={3}>
                                                    <Text fontSize="4xl">🛌</Text>
                                                    <Text fontWeight="600" fontSize="lg" color={useColorModeValue('orange.800', 'orange.100')}>
                                                        Rest and Recovery Day
                                                    </Text>
                                                    <Text fontSize="sm" color={useColorModeValue('orange.700', 'orange.200')} textAlign="center">
                                                        {selectedEvent.notes || 'Take time to rest, recover, and prepare for your next workout session.'}
                                                    </Text>
                                                </VStack>
                                            </CardBody>
                                        </Card>
                                    </Box>
                                )}

                                {/* General Notes */}
                                {selectedEvent.notes && !selectedEvent.dailyWorkout?.is_rest_day && (
                                    <Box>
                                        <Text fontSize="sm" color={useColorModeValue('gray.600', 'gray.400')} mb={2} fontWeight="600">
                                            📝 Additional Notes
                                        </Text>
                                        <Card borderRadius="lg" bg={useColorModeValue('white', 'navy.700')}>
                                            <CardBody>
                                                <Text fontSize="sm">{selectedEvent.notes}</Text>
                                            </CardBody>
                                        </Card>
                                    </Box>
                                )}
                            </VStack>
                        )}
                    </ModalBody>

                    <ModalFooter borderTop="1px" borderColor={useColorModeValue('gray.200', 'navy.600')}>
                        <HStack spacing={3} width="100%">
                            <Button variant="outline" onClick={onClose} flex={1}>
                                Close
                            </Button>
                            {selectedEvent && selectedEvent.dailyWorkout && (
                                <Button
                                    colorScheme="green"
                                    onClick={() => {
                                        onDailyWorkoutEditorOpen();
                                        onClose();
                                    }}
                                    flex={1}
                                    leftIcon={<Icon as={MdEdit} />}
                                    isDisabled={selectedEvent.dailyWorkout.is_rest_day}
                                >
                                    Edit Workout
                                </Button>
                            )}

                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Daily Workout Editor Modal */}
            <DailyWorkoutEditor
                isOpen={isDailyWorkoutEditorOpen}
                onClose={onDailyWorkoutEditorClose}
                dailyWorkout={selectedEvent?.dailyWorkout || null}
                onWorkoutUpdated={refreshWorkoutPlans}
            />

            {/* Exercise Selector for Quick Add Mode */}
            <ExerciseSelector
                isOpen={isExerciseSelectorOpen}
                onClose={() => {
                    setIsExerciseSelectorOpen(false);
                    setSelectedDayForExercises(null);
                }}
                onExercisesSelected={handleExercisesSelected}
            />
        </SimpleGrid>
    );
};

export default ClientWorkoutCalendar;